import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { VideoCard } from '../components/VideoCard';
import {
  getCreatorVideos,
  getCreatorAnalytics,
  uploadVideo,
  getMyPayouts,
  requestPayout
} from '../api';
import { Upload, Eye, Clock, DollarSign, TrendingUp, Link, FileVideo, Film } from 'lucide-react';
import { toast } from 'sonner';

export const CreatorStudioPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'youtube' | 'facebook'
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general',
    quality: 'hd',
    status: 'new',
    youtube_url: '',
    facebook_url: '',
    video_file: null,
    thumbnail: null
  });

  const CATEGORIES = [
    { value: 'general', label: '🎬 Général' },
    { value: 'music', label: '🎵 Musique' },
    { value: 'sports', label: '⚽ Sports' },
    { value: 'education', label: '📚 Éducation' },
    { value: 'entertainment', label: '🎭 Divertissement' },
    { value: 'news', label: '📰 Actualités' },
    { value: 'documentary', label: '🎞️ Documentaire' },
    { value: 'short_film', label: '🎥 Court métrage' },
    { value: 'feature_film', label: '🎦 Long métrage' },
    { value: 'animation', label: '🎨 Animation' },
    { value: 'reality_tv', label: '📺 Téléréalité' },
    { value: 'show', label: '🎤 Émission' },
    { value: 'comedy', label: '😂 Comédie' },
    { value: 'religion', label: '✝️ Religion' },
    { value: 'cooking', label: '🍳 Cuisine' },
    { value: 'fashion', label: '👗 Mode' },
    { value: 'tech', label: '💻 Technologie' },
  ];

  const QUALITIES = [
    { value: 'sd', label: 'SD — 480p' },
    { value: 'hd', label: 'HD — 720p' },
    { value: 'fullhd', label: 'Full HD — 1080p' },
    { value: '4k', label: '4K — 2160p' },
  ];

  useEffect(() => {
    if (!user || (user.role !== 'creator' && user.role !== 'superadmin')) {
      navigate('/');
      return;
    }
    fetchVideos();
    fetchAnalytics();
    fetchPayouts();
  }, [user]);

  const fetchVideos = async () => {
    try {
      const response = await getCreatorVideos();
      setVideos(response.data.videos);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await getCreatorAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await getMyPayouts();
      setPayouts(response.data.payouts);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };

  const handleFileChange = (e, field) => {
    setUploadForm({ ...uploadForm, [field]: e.target.files[0] });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('quality', uploadForm.quality);
      formData.append('status', uploadForm.status);

      if (uploadMode === 'youtube') {
        if (!uploadForm.youtube_url.trim()) {
          toast.error('Veuillez entrer un lien YouTube');
          return;
        }
        formData.append('youtube_url', uploadForm.youtube_url.trim());
        // Thumbnail optionnelle en mode YouTube (auto-extraite)
        if (uploadForm.thumbnail) {
          formData.append('thumbnail', uploadForm.thumbnail);
        }
      } else if (uploadMode === 'facebook') {
        if (!uploadForm.facebook_url.trim()) {
          toast.error('Veuillez entrer un lien Facebook');
          return;
        }
        formData.append('facebook_url', uploadForm.facebook_url.trim());
        if (!uploadForm.thumbnail) {
          toast.error('Une thumbnail est requise pour les vidéos Facebook');
          return;
        }
        formData.append('thumbnail', uploadForm.thumbnail);
      } else {
        // Mode fichier
        if (!uploadForm.video_file) {
          toast.error('Veuillez sélectionner un fichier vidéo');
          return;
        }
        if (!uploadForm.thumbnail) {
          toast.error('Veuillez sélectionner une thumbnail');
          return;
        }
        formData.append('video_file', uploadForm.video_file);
        formData.append('thumbnail', uploadForm.thumbnail);
      }

      await uploadVideo(formData);
      toast.success('Vidéo publiée avec succès !');
      setUploadForm({
        title: '',
        description: '',
        category: 'general',
        quality: 'hd',
        status: 'new',
        youtube_url: '',
        facebook_url: '',
        video_file: null,
        thumbnail: null
      });
      setActiveTab('videos');
      fetchVideos();
      fetchAnalytics();
    } catch (error) {
      console.error('Upload failed:', error);
      const msg = error?.response?.data?.detail || 'Erreur lors de l\'upload';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const payoutData = {
      amount: formData.get('amount'),
      provider: formData.get('provider'),
      phone_number: formData.get('phone')
    };

    try {
      await requestPayout(payoutData);
      toast.success('Demande de paiement créée avec succès');
      e.target.reset();
      fetchPayouts(); // Refresh the payouts list
    } catch (error) {
      console.error('Payout request failed:', error);
      toast.error('Erreur lors de la demande de paiement');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8">Studio Créateur</h1>

        {/* Analytics KPIs */}
        {analytics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div
              className="bg-card border border-border rounded-xl p-4"
              data-testid="creator-kpi-views"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Eye className="w-4 h-4" />
                <span className="text-xs font-medium">Vues totales</span>
              </div>
              <p className="text-2xl font-bold">{analytics.total_views}</p>
            </div>

            <div
              className="bg-card border border-border rounded-xl p-4"
              data-testid="creator-kpi-watchtime"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">Watch-time</span>
              </div>
              <p className="text-2xl font-bold">
                {Math.floor(analytics.total_watch_time / 60)} min
              </p>
            </div>

            <div
              className="bg-card border border-border rounded-xl p-4"
              data-testid="creator-kpi-revenue"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium">Revenus estimés</span>
              </div>
              <p className="text-2xl font-bold">{analytics.estimated_revenue} CDF</p>
            </div>

            <div
              className="bg-card border border-border rounded-xl p-4"
              data-testid="creator-kpi-rpm"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">RPM</span>
              </div>
              <p className="text-2xl font-bold">{analytics.rpm}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="creator-studio-tabs">
          <button
            onClick={() => setActiveTab('videos')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'videos'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Mes vidéos ({videos.length})
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'upload'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Upload
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'payouts'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Paiements
          </button>
        </div>

        {/* Content */}
        {activeTab === 'videos' && (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-5"
            data-testid="creator-videos-table"
          >
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="max-w-2xl">
            <form
              onSubmit={handleUpload}
              className="bg-card border border-border rounded-xl p-6 space-y-5"
              data-testid="creator-upload-form"
            >
              <h2 className="text-xl font-semibold">Publier une vidéo</h2>

              {/* MODE SELECTOR */}
              <div className="flex gap-2 p-1 bg-muted rounded-lg">
                <button
                  type="button"
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'file' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <FileVideo className="w-4 h-4" /> Fichier vidéo
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('youtube')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'youtube' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  YouTube
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('facebook')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'facebook' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </button>
              </div>

              {/* TITRE */}
              <div>
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  required
                  placeholder="Titre de votre vidéo"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  required
                  rows={3}
                  placeholder="Décrivez votre vidéo..."
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              {/* CATÉGORIE + QUALITÉ */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Catégorie *</label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {uploadMode === 'file' ? 'Qualité de conversion' : 'Format déclaré'}
                  </label>
                  <select
                    value={uploadForm.quality}
                    onChange={(e) => setUploadForm({ ...uploadForm, quality: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {QUALITIES.map(q => (
                      <option key={q.value} value={q.value}>{q.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* STATUT */}
              <div>
                <label className="block text-sm font-medium mb-1">Statut</label>
                <select
                  value={uploadForm.status}
                  onChange={(e) => setUploadForm({ ...uploadForm, status: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="new">🆕 Nouveau</option>
                  <option value="soon">⏳ Bientôt disponible</option>
                </select>
              </div>

              {/* SOURCE VIDÉO selon mode */}
              {uploadMode === 'youtube' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Lien YouTube *</label>
                  <input
                    type="url"
                    value={uploadForm.youtube_url}
                    onChange={(e) => setUploadForm({ ...uploadForm, youtube_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground mt-1">La thumbnail sera extraite automatiquement depuis YouTube</p>
                </div>
              )}

              {uploadMode === 'facebook' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Lien Facebook *</label>
                  <input
                    type="url"
                    value={uploadForm.facebook_url}
                    onChange={(e) => setUploadForm({ ...uploadForm, facebook_url: e.target.value })}
                    placeholder="https://www.facebook.com/watch?v=..."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

              {uploadMode === 'file' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Fichier vidéo *</label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileChange(e, 'video_file')}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">MP4, MKV, AVI, MOV... (max 2 Go recommandé)</p>
                </div>
              )}

              {/* THUMBNAIL */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Thumbnail {uploadMode === 'youtube' ? '(optionnelle — auto-extraite)' : '*'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'thumbnail')}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm"
                />
                {uploadForm.thumbnail && (
                  <div className="mt-2">
                    <img
                      src={URL.createObjectURL(uploadForm.thumbnail)}
                      alt="Aperçu thumbnail"
                      className="h-24 rounded-lg object-cover border border-border"
                    />
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                {uploading ? 'Publication en cours...' : 'Publier la vidéo'}
              </button>

              {uploading && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    {uploadMode === 'file' ? 'Conversion et upload en cours, cela peut prendre plusieurs minutes...' : 'Publication en cours...'}
                  </p>
                  <div className="mt-2 w-full bg-muted rounded-full h-1.5">
                    <div className="bg-primary h-1.5 rounded-full animate-pulse w-2/3" />
                  </div>
                </div>
              )}
            </form>
          </div>
        )}

        {activeTab === 'payouts' && (
          <div className="max-w-4xl space-y-6">
            {/* Payout Request Form */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">Demander un paiement</h2>
              <form onSubmit={handleRequestPayout} className="space-y-4" data-testid="creator-payout-form">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Montant (CDF) - Minimum 100 CDF
                  </label>
                  <input
                    type="number"
                    name="amount"
                    min="100"
                    step="0.01"
                    required
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="100.00"
                  />
                  {analytics && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Disponible: {analytics.estimated_revenue} CDF
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Provider</label>
                  <select
                    name="provider"
                    required
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="mpesa">M-Pesa (Vodacom/Airtel)</option>
                    <option value="orange">Orange Money</option>
                    <option value="africell">Africell Money</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Numéro de téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="+243..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  Demander le paiement
                </button>
              </form>
            </div>

            {/* Payout History */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">
                Historique des paiements ({payouts.length})
              </h2>
              {payouts.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Aucune demande de paiement
                </p>
              ) : (
                <div className="space-y-3">
                  {payouts.map((payout) => (
                    <div
                      key={payout.id}
                      className="flex items-center justify-between p-4 bg-background rounded-lg border border-border"
                    >
                      <div>
                        <p className="font-medium">{payout.amount} CDF</p>
                        <p className="text-sm text-muted-foreground">
                          {payout.provider} • {payout.phone_number}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(payout.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            payout.status === 'completed'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                              : payout.status === 'rejected'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                              : payout.status === 'processing'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                          }`}
                        >
                          {payout.status === 'pending' && 'En attente'}
                          {payout.status === 'approved' && 'Approuvé'}
                          {payout.status === 'processing' && 'En cours'}
                          {payout.status === 'completed' && 'Complété'}
                          {payout.status === 'rejected' && 'Rejeté'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
